import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
    Table, Button, Select, Input, Row, Col,
    Form, DatePicker, InputNumber, Empty, Space,
    Collapse, Tag,
} from 'antd';
import {
    ReloadOutlined, SearchOutlined, ClearOutlined,
    FolderOpenOutlined, FileTextOutlined, WalletOutlined,
    CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined,
    BankOutlined, CalendarOutlined, DownOutlined, RightOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, formatNumber } from '../../variables/constants';
import TableExportButton from '../../components/TableExportButton';
import KpiCards from '../../components/KpiCards';
import logoLocal from '../../assets/images/logo.png';
import PageHeader from '../../components/PageHeader';
import { montantEffectif, nombreReglements } from './reglementsChaine';
import SelectBanque from '../../components/SelectBanque';

import { useTranslation, Trans } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import { avecLibelleTraduit } from '../../i18n/libelles';

const { Option } = Select;
const { RangePicker } = DatePicker;

/*  Tokens  */
const G = {
    green: '#1a6b3c',
    greenLight: '#e8f5ee',
    greenBorder: '#a7d4b8',
    greenDark: '#14532d',
    neutral50: '#f9fafb',
    neutral100: '#f3f4f6',
    neutral200: '#e5e7eb',
    neutral400: '#9ca3af',
    neutral500: '#6b7280',
    neutral700: '#374151',
    neutral900: '#111827',
    shadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 12px rgba(0,0,0,.04)',
};

/*  Référentiels  */
const STATUTS_PAIEMENT = avecLibelleTraduit([
    { value: 'EMIS', color: 'blue', icon: <ClockCircleOutlined /> },
    { value: 'REGLEMENT_TECHNIQUE_VALIDE', color: 'orange', icon: <ClockCircleOutlined /> },
    { value: 'REGLEMENT_COMPTABLE_VALIDE', color: 'geekblue', icon: <ClockCircleOutlined /> },
    { value: 'PAYE', color: 'green', icon: <CheckCircleOutlined /> },
    { value: 'ANNULE', color: 'red', icon: <CloseCircleOutlined /> },
], 'paiements:statuts');

const CATEGORIES = avecLibelleTraduit([
    { value: 'PRINCIPAL' },
    { value: 'HONORAIRES' },
    { value: 'FRAIS_ACCESSOIRES' },
    { value: 'PROVISIONS' },
], 'paiements:categories');

const STATUT_MAP = STATUTS_PAIEMENT.reduce((acc, s) => (acc[s.value] = s, acc), {});

/*  Repartition des criteres de recherche.

    Ce que cherche un comptable en premier, c'est le dossier ou la personne, puis l'etat
    du reglement : ces trois criteres restent visibles en permanence. Les references de
    piece (n° paiement, n° cheque), la banque, la categorie, les montants et les periodes
    servent au rapprochement bancaire ou au reporting mensuel, beaucoup plus rarement :
    ils sont replies. Aucun critere n'est supprime, la requete envoyee est identique. */
const CHAMPS_ESSENTIELS = ['numeroSinistreLocal', 'beneficiaire', 'statut'];
const CHAMPS_AVANCES = [
    'numeroPaiement', 'numeroChequeEmis', 'banqueCheque', 'categorie',
    'montantMin', 'montantMax', 'dateEmission', 'datePaiement',
];

/** Un critere compte comme actif s'il porte une valeur exploitable.
 *  Les RangePicker renvoient un tableau (parfois [null, null]) : on l'aplatit. */
function estRenseigne(v) {
    if (Array.isArray(v)) return v.filter(Boolean).length > 0;
    return v !== undefined && v !== null && v !== '';
}

/*  Helpers  */
async function fetchCount(statut) {
    try {
        const res = await axiosInstance.get('/v1/paiements/search', {
            params: { statut, size: 1, page: 0 },
        });
        return res.data?.totalElements ?? 0;
    } catch { return 0; }
}

/** Regroupe un tableau plat de paiements par sinistreReference */
function groupBySinistre(paiements) {
    const map = new Map();
    for (const p of paiements) {
        const ref = p.sinistreReference || '-';
        if (!map.has(ref)) {
            map.set(ref, {
                sinistreReference: ref,
                sinistreTrackingId: p.sinistreTrackingId,
                paiements: [],
                totalMontant: 0,
                nbPaye: 0,
                nbEnCours: 0,
                nbAnnule: 0,
            });
        }
        const g = map.get(ref);
        g.paiements.push(p);
        if (p.statut === 'PAYE') g.nbPaye++;
        else if (p.statut === 'ANNULE') g.nbAnnule++;
        else g.nbEnCours++;
    }
    // Le total se calcule une fois le groupe complet : le règlement comptable est une
    // copie du technique, il ne doit pas s'additionner à lui (voir reglementsChaine).
    return Array.from(map.values()).map((g) => ({ ...g, totalMontant: montantEffectif(g.paiements) }));
}

/*  Styles injectés  */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .pmt-root, .pmt-root * { font-family: 'DM Sans', sans-serif; }
  .pmt-root .mono { font-family: var(--font-main); font-variant-numeric: tabular-nums; }

  .pmt-sinistre-row {
    background: #fff !important;
    cursor: pointer;
    transition: background .15s;
  }
  .pmt-sinistre-row:hover td { background: ${G.greenLight} !important; }

  .pmt-sinistre-row td {
    border-bottom: 1px solid ${G.neutral100} !important;
    padding: 12px 16px !important;
    vertical-align: middle !important;
  }

  /* Sous-tableau paiements */
  .pmt-sub-table .ant-table {
    margin: 0 !important;
    border-radius: 0 !important;
  }
  .pmt-sub-table .ant-table-thead th {
    background: ${G.neutral50} !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    color: ${G.neutral500} !important;
    text-transform: uppercase !important;
    letter-spacing: .6px !important;
    border-bottom: 1px solid ${G.neutral200} !important;
    padding: 8px 14px !important;
  }
  .pmt-sub-table .ant-table-tbody td {
    font-size: 12.5px !important;
    padding: 9px 14px !important;
    border-bottom: 1px solid ${G.neutral100} !important;
  }
  .pmt-sub-table .ant-table-tbody tr:last-child td {
    border-bottom: none !important;
  }
  .pmt-sub-table .ant-table-tbody tr:hover td {
    background: ${G.greenLight} !important;
  }

  /* Ligne expanded */
  .ant-table-expanded-row > td {
    background: ${G.neutral50} !important;
    padding: 0 !important;
    border-bottom: 2px solid ${G.greenBorder} !important;
  }

  /* Expand icon */
  .ant-table-row-expand-icon {
    border-color: ${G.greenBorder} !important;
    color: ${G.green} !important;
  }
  .ant-table-row-expand-icon:hover {
    color: ${G.green} !important;
    border-color: ${G.green} !important;
  }

  .pmt-root .ant-btn-primary {
    background: ${G.green} !important;
    border-color: ${G.green} !important;
  }
  .pmt-root .ant-btn-primary:hover {
    background: #2d8653 !important;
    border-color: #2d8653 !important;
  }
  .pmt-root .ant-pagination-item-active {
    border-color: ${G.green} !important;
  }
  .pmt-root .ant-pagination-item-active a { color: ${G.green} !important; }
  .pmt-root .ant-table-thead th {
    background: ${G.neutral50} !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    color: ${G.neutral500} !important;
    text-transform: uppercase !important;
    letter-spacing: .5px !important;
  }

  .sinistre-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
  }
  .pmt-expand-panel {
    border-left: 3px solid ${G.green};
        border-right: 3px solid ${G.green};
            border-top: 3px solid ${G.green};
                border-bottom: 3px solid ${G.green};
    margin: 0;
  }
  .pmt-sub-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: ${G.greenLight};
    border-top: 1px solid ${G.greenBorder};
    font-size: 12px;
    color: ${G.neutral700};
  }
`;

/*  Sous-tableau des paiements d'un sinistre  */
function PaiementsSousTableau({ paiements }) {
    const { t } = useTranslation('paiements');
    const cols = [
        {
            title: t('sousTableau.colonnes.numeroPaiement'),
            dataIndex: 'numeroPaiement',
            key: 'num',
            width: 170,
            render: v => (
                <span className="mono" style={{ fontWeight: 600, fontSize: 12, color: G.neutral900 }}>
                    {v}
                </span>
            ),
        },
        {
            title: t('sousTableau.colonnes.beneficiaire'),
            dataIndex: 'beneficiaire',
            key: 'ben',
            ellipsis: true,
            width: 160,
            render: v => v || <span style={{ color: G.neutral400 }}>-</span>,
        },
        {
            title: t('sousTableau.colonnes.categorie'),
            dataIndex: 'categorie',
            key: 'cat',
            width: 140,
            render: v => {
                const c = CATEGORIES.find(x => x.value === v);
                return c
                    ? <span style={{
                        padding: '2px 8px', borderRadius: 12,
                        background: G.neutral100, color: G.neutral700,
                        fontSize: 11, fontWeight: 600, border: `1px solid ${G.neutral200}`,
                    }}>{c.label}</span>
                    : <span style={{ color: G.neutral400 }}>-</span>;
            },
        },
        {
            title: t('sousTableau.colonnes.banque'),
            dataIndex: 'banqueCheque',
            key: 'bq',
            width: 120,
            ellipsis: true,
            render: v => v
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <BankOutlined style={{ fontSize: 10, color: G.neutral400 }} />
                    {v}
                </span>
                : <span style={{ color: G.neutral400 }}>-</span>,
        },
        {
            title: t('sousTableau.colonnes.emission'),
            dataIndex: 'dateEmission',
            key: 'dem',
            width: 105,
            render: v => v
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <CalendarOutlined style={{ fontSize: 10, color: G.neutral400 }} />
                    {formatDate(v)}
                </span>
                : '-',
        },
        {
            title: t('sousTableau.colonnes.paiement'),
            dataIndex: 'datePaiement',
            key: 'dpay',
            width: 105,
            render: v => v
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                    <CalendarOutlined style={{ fontSize: 10, color: G.neutral400 }} />
                    {formatDate(v)}
                </span>
                : '-',
        },
        {
            title: t('sousTableau.colonnes.montant'),
            dataIndex: 'montant',
            key: 'mt',
            width: 145,
            align: 'right',
            render: v => (
                <span className="mono" style={{ fontWeight: 700, color: G.neutral900, fontSize: 13 }}>
                    {formatNumber(v)}
                </span>
            ),
        },
        {
            title: t('sousTableau.colonnes.statut'),
            dataIndex: 'statut',
            key: 'st',
            width: 145,
            render: v => {
                const s = STATUT_MAP[v] || { label: v, color: 'default', icon: null };
                return (
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 10px', borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                        ...getStatutStyle(v),
                    }}>
                        {s.icon && React.cloneElement(s.icon, { style: { fontSize: 10 } })}
                        {s.label}
                    </span>
                );
            },
        },
    ];

    return (
        <div className="pmt-expand-panel">
            <div className="pmt-sub-table">
                <Table
                    dataSource={paiements}
                    columns={cols}
                    rowKey="paiementTrackingId"
                    pagination={false}
                    size="small"
                    scroll={{ x: 950 }}
                />
            </div>
            <div className="pmt-sub-footer">
                <span style={{ color: G.neutral500 }}>
                    <FileTextOutlined style={{ marginRight: 5 }} />
                    {t('compteReglements', { count: nombreReglements(paiements) })}
                    {paiements.length > nombreReglements(paiements) && (
                        <span style={{ color: G.neutral400 }}> {t('sousTableau.lignes', { n: paiements.length })}</span>
                    )}
                </span>
                <span style={{ fontWeight: 700, color: G.green, fontSize: 13 }}>
                    <WalletOutlined style={{ marginRight: 5 }} />
                    {t('sousTableau.total', { montant: formatNumber(montantEffectif(paiements)) })}
                </span>
            </div>
        </div>
    );
}

function getStatutStyle(v) {
    const map = {
        EMIS: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' },
        REGLEMENT_TECHNIQUE_VALIDE: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c' },
        REGLEMENT_COMPTABLE_VALIDE: { background: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3' },
        PAYE: { background: G.greenLight, border: `1px solid ${G.greenBorder}`, color: G.green },
        ANNULE: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' },
    };
    return map[v] || { background: G.neutral100, border: `1px solid ${G.neutral200}`, color: G.neutral500 };
}

/*  Page principale  */
export default function Paiements() {
    const navigate = useNavigate();
    const { t } = useTranslation('paiements');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 100, total: 0 });
    const [filters, setFilters] = useState({});
    const [kpis, setKpis] = useState(null);
    const [expandedRows, setExpandedRows] = useState([]);
    const [searchForm] = Form.useForm();
    /* Copie des valeurs saisies : sert uniquement a l'affichage (compteur du tiroir,
       bouton Reinitialiser). La recherche continue de lire le formulaire via onFinish. */
    const [valeursSaisies, setValeursSaisies] = useState({});

    /* KPIs */
    const loadKpis = useCallback(async () => {
        setKpis(null);
        const [nbEmis, nbTechValide, nbComptaValide, nbPaye, nbAnnule] = await Promise.all([
            fetchCount('EMIS'),
            fetchCount('REGLEMENT_TECHNIQUE_VALIDE'),
            fetchCount('REGLEMENT_COMPTABLE_VALIDE'),
            fetchCount('PAYE'),
            fetchCount('ANNULE'),
        ]);
        const enCours = nbEmis + nbTechValide + nbComptaValide;
        const total = enCours + nbPaye + nbAnnule;
        setKpis({ total, nbPaye, enCours, nbAnnule });
    }, []);

    /* Export : récupère toutes les pages */
    const fetchAll = useCallback(async () => {
        const hasFilter = Object.values(filters).some(v => v !== undefined && v !== null && v !== '');
        const url = hasFilter ? '/v1/paiements/search' : '/v1/paiements';
        const SIZE = 200;
        const first = await axiosInstance.get(url, { params: { ...(hasFilter ? filters : {}), page: 0, size: SIZE } });
        const all = [...(first.data?.data || [])];
        const totalPages = first.data?.totalPages ?? 1;
        if (totalPages > 1) {
            const rest = await Promise.all(
                Array.from({ length: totalPages - 1 }, (_, i) => i + 1).map(p =>
                    axiosInstance.get(url, { params: { ...(hasFilter ? filters : {}), page: p, size: SIZE } })
                )
            );
            rest.forEach(r => all.push(...(r.data?.data || [])));
        }
        return all;
    }, [filters]);

    /* Chargement tableau - on demande une grande page pour regrouper côté client */
    const load = useCallback(async (page = 0, size = 100, criteria = {}) => {
        setLoading(true);
        try {
            const hasFilter = Object.values(criteria).some(v => v !== undefined && v !== null && v !== '');
            const url = hasFilter ? '/v1/paiements/search' : '/v1/paiements';
            const res = await axiosInstance.get(url, { params: { page, size, ...(hasFilter ? criteria : {}) } });
            const body = res.data || {};
            setItems(body.data || []);
            setPagination({
                current: (body.currentPage ?? 0) + 1,
                pageSize: body.pageSize ?? size,
                total: body.totalElements ?? 0,
            });
        } catch {
            setItems([]);
            setPagination(p => ({ ...p, total: 0 }));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(0, 100, {}); loadKpis(); }, [load, loadKpis]);

    /* Groupement */
    const groupes = useMemo(() => groupBySinistre(items), [items]);

    const onTableChange = (pag) => load(pag.current - 1, pag.pageSize, filters);

    const onSearch = (vals) => {
        const criteria = {
            numeroPaiement: vals.numeroPaiement || undefined,
            numeroChequeEmis: vals.numeroChequeEmis || undefined,
            beneficiaire: vals.beneficiaire || undefined,
            banqueCheque: vals.banqueCheque || undefined,
            statut: vals.statut || undefined,
            categorie: vals.categorie || undefined,
            sinistreTrackingId: vals.sinistreTrackingId || undefined,
            numeroSinistreLocal: vals.numeroSinistreLocal || undefined,
            montantMin: vals.montantMin ?? undefined,
            montantMax: vals.montantMax ?? undefined,
            dateEmissionDebut: vals.dateEmission?.[0]?.format('YYYY-MM-DD') || undefined,
            dateEmissionFin: vals.dateEmission?.[1]?.format('YYYY-MM-DD') || undefined,
            datePaiementDebut: vals.datePaiement?.[0]?.format('YYYY-MM-DD') || undefined,
            datePaiementFin: vals.datePaiement?.[1]?.format('YYYY-MM-DD') || undefined,
        };
        setFilters(criteria);
        setExpandedRows([]);
        load(0, 100, criteria);
    };

    const onReset = () => {
        searchForm.resetFields();
        /* resetFields ne declenche pas onValuesChange : on remet la copie a plat nous-memes. */
        setValeursSaisies({});
        setFilters({});
        setExpandedRows([]);
        load(0, 100, {});
    };

    const onRefresh = () => { load(pagination.current - 1, pagination.pageSize, filters); loadKpis(); };

    const nbFiltresAvances = CHAMPS_AVANCES.filter(c => estRenseigne(valeursSaisies[c])).length;
    /* On propose « Reinitialiser » aussi quand le formulaire est vide mais qu'une recherche
       reste appliquee (ex. apres un rechargement) : sinon le seul moyen de revenir a la
       liste complete serait invisible. */
    const peutReinitialiser =
        nbFiltresAvances > 0
        || CHAMPS_ESSENTIELS.some(c => estRenseigne(valeursSaisies[c]))
        || Object.values(filters).some(v => v !== undefined && v !== null && v !== '');

    /* KPI cards */
    const kpiItems = kpis
        ? [
            { label: t('kpi.total'), value: kpis.total.toLocaleString(localeIntl()), color: G.neutral700, bg: G.neutral100 },
            { label: t('kpi.payes'), value: kpis.nbPaye.toLocaleString(localeIntl()), color: '#15803d', bg: '#f0fdf4' },
            { label: t('kpi.enCours'), value: kpis.enCours.toLocaleString(localeIntl()), color: '#2563eb', bg: '#eff6ff' },
            { label: t('kpi.annules'), value: kpis.nbAnnule.toLocaleString(localeIntl()), color: '#dc2626', bg: '#fef2f2' },
        ]
        : [
            { label: t('kpi.total'), value: '-', color: G.neutral400, bg: G.neutral50 },
            { label: t('kpi.payes'), value: '-', color: G.neutral400, bg: G.neutral50 },
            { label: t('kpi.enCours'), value: '-', color: G.neutral400, bg: G.neutral50 },
            { label: t('kpi.annules'), value: '-', color: G.neutral400, bg: G.neutral50 },
        ];

    /* Colonnes du tableau principal (sinistres) */
    const sinistresColumns = [
        {
            title: t('tableau.numeroSinistre'),
            dataIndex: 'sinistreReference',
            key: 'ref',
            width: 170,
            // Le numéro ouvre le dossier : depuis le règlement, le réflexe est
            // toujours d'aller voir le sinistre - autant que ce soit un clic.
            render: (v, r) => (
                r.sinistreTrackingId ? (
                    <a
                        href={`/sinistres/${r.sinistreTrackingId}`}
                        className="mono"
                        style={{ fontWeight: 700, color: G.green, fontSize: 13, textDecoration: 'underline' }}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/sinistres/${r.sinistreTrackingId}`); }}
                        title={t('tableau.ouvrirDossier')}
                    >
                        {v}
                    </a>
                ) : (
                    <span className="mono" style={{ fontWeight: 700, color: G.green, fontSize: 13 }}>{v}</span>
                )
            ),
        },
        {
            title: t('tableau.reglements'),
            key: 'nb',
            width: 180,
            render: (_, r) => (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '3px 10px', borderRadius: 20,
                    background: G.greenLight, border: `1px solid ${G.greenBorder}`,
                    color: G.green, fontSize: 12, fontWeight: 600,
                }}>
                    <FileTextOutlined style={{ fontSize: 10 }} />
                    {t('compteReglements', { count: r.paiements.length })}
                </span>
            ),
        },
        {
            title: t('tableau.statuts'),
            key: 'statuts',
            width: 350,
            render: (_, r) => (
                <Space size={5} wrap>
                    {r.nbPaye > 0 && (
                        <span className="sinistre-badge" style={{ background: G.greenLight, border: `1px solid ${G.greenBorder}`, color: G.green }}>
                            <CheckCircleOutlined style={{ fontSize: 10 }} />
                            {t('tableau.payes', { count: r.nbPaye })}
                        </span>
                    )}
                    {r.nbEnCours > 0 && (
                        <span className="sinistre-badge" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}>
                            <ClockCircleOutlined style={{ fontSize: 10 }} />
                            {t('tableau.enCours', { n: r.nbEnCours })}
                        </span>
                    )}
                    {r.nbAnnule > 0 && (
                        <span className="sinistre-badge" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
                            <CloseCircleOutlined style={{ fontSize: 10 }} />
                            {t('tableau.annules', { count: r.nbAnnule })}
                        </span>
                    )}
                </Space>
            ),
        },
        {
            title: t('tableau.total'),
            key: 'total',
            align: 'right',
            render: (_, r) => (
                <span className="mono" style={{ fontWeight: 700, fontSize: 15, color: G.neutral900 }}>
                    {formatNumber(r.totalMontant)}
                </span>
            ),
        },
    ];

    return (
        <>
            <style>{CSS}</style>
            <Layout title={t('titre')}>
                <div className="pmt-root">

                    {/* En-tête */}
                    {/* `pagination.total` = totalElements renvoye par le backend pour les
                        filtres courants. Le tableau affiche `groupes` (regroupement par
                        dossier), dont la longueur compterait des groupes, pas des reglements. */}
                    <PageHeader
                        titre={t('titre')}
                        icone={<WalletOutlined />}
                        compte={pagination.total}
                        unite={t('unite')}
                        unitePluriel={t('unitePluriel')}
                        actions={<Button icon={<ReloadOutlined />} onClick={onRefresh} style={{ borderRadius: 8 }} />}
                    />

                    {/* KPIs */}
                    <KpiCards items={kpiItems} columns={4} />

                    {/* Filtres */}
                    <div className="cb-card" style={{ padding: 16, marginBottom: 16 }}>
                        <Form
                            form={searchForm}
                            layout="vertical"
                            onFinish={onSearch}
                            onValuesChange={(_, toutes) => setValeursSaisies(toutes)}
                        >
                            {/* Ligne essentielle : les libelles passent en placeholder pour tenir
                                sur une seule hauteur de champ. */}
                            <Row gutter={12} align="middle">
                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item name="numeroSinistreLocal" style={{ marginBottom: 0 }}>
                                        <Input allowClear placeholder={t('filtres.numeroSinistre')} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={7}>
                                    <Form.Item name="beneficiaire" style={{ marginBottom: 0 }}>
                                        <Input allowClear placeholder={t('filtres.beneficiaire')} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={5}>
                                    <Form.Item name="statut" style={{ marginBottom: 0 }}>
                                        <Select allowClear placeholder={t('filtres.tousStatuts')} style={{ width: '100%' }}>
                                            {STATUTS_PAIEMENT.map(s => (
                                                <Option key={s.value} value={s.value}>{s.label}</Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Space>
                                        <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                                            {t('filtres.rechercher')}
                                        </Button>
                                        {/* Rien a effacer, pas de bouton : il n'apparait que s'il sert. */}
                                        {peutReinitialiser && (
                                            <Button icon={<ClearOutlined />} onClick={onReset}>
                                                {t('filtres.reinitialiser')}
                                            </Button>
                                        )}
                                    </Space>
                                </Col>
                            </Row>

                            {/* Tiroir des criteres de rapprochement / reporting.
                                forceRender monte les champs des le depart : le formulaire garde
                                ainsi le meme comportement (reset, valeurs) tiroir ouvert ou non. */}
                            <Collapse
                                bordered={false}
                                expandIconPosition="end"
                                style={{ background: 'transparent', marginTop: 12 }}
                                items={[{
                                    key: 'avances',
                                    forceRender: true,
                                    style: {
                                        background: '#fff',
                                        border: `1px solid ${G.neutral200}`,
                                        borderRadius: 8,
                                    },
                                    label: (
                                        <span style={{ fontSize: 13, fontWeight: 700, color: G.neutral700 }}>
                                            {t('filtres.avances')}
                                            {nbFiltresAvances > 0 && (
                                                <Tag color="green" style={{ marginLeft: 8, borderRadius: 12 }}>
                                                    {t('filtres.actifs', { count: nbFiltresAvances })}
                                                </Tag>
                                            )}
                                        </span>
                                    ),
                                    children: (
                                        <Row gutter={12}>
                                            <Col xs={24} sm={12} md={6}>
                                                <Form.Item name="numeroPaiement" label={t('filtres.numeroPaiement')} style={{ marginBottom: 8 }}>
                                                    <Input allowClear placeholder="RT-2025…" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={6}>
                                                <Form.Item name="numeroChequeEmis" label={t('filtres.numeroCheque')} style={{ marginBottom: 8 }}>
                                                    <Input allowClear placeholder="CHQ-…" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={6}>
                                                <Form.Item name="banqueCheque" label={t('filtres.banque')} style={{ marginBottom: 8 }}>
                                                    <SelectBanque placeholder={t('filtres.banqueEmettrice')} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={6}>
                                                <Form.Item name="categorie" label={t('filtres.categorie')} style={{ marginBottom: 8 }}>
                                                    <Select allowClear placeholder={t('filtres.toutes')}>
                                                        {CATEGORIES.map(c => (
                                                            <Option key={c.value} value={c.value}>{c.label}</Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={4}>
                                                <Form.Item name="montantMin" label={t('filtres.montantMin')} style={{ marginBottom: 8 }}>
                                                    <InputNumber
                                                        style={{ width: '100%' }} min={0} step={1000}
                                                        formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={4}>
                                                <Form.Item name="montantMax" label={t('filtres.montantMax')} style={{ marginBottom: 8 }}>
                                                    <InputNumber
                                                        style={{ width: '100%' }} min={0} step={1000}
                                                        formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={8}>
                                                <Form.Item name="dateEmission" label={t('filtres.periodeEmission')} style={{ marginBottom: 8 }}>
                                                    <RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} sm={12} md={8}>
                                                <Form.Item name="datePaiement" label={t('filtres.periodePaiement')} style={{ marginBottom: 8 }}>
                                                    <RangePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ),
                                }]}
                            />
                        </Form>
                    </div>

                    {/* Légende */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
                        fontSize: 12, color: G.neutral500,
                    }}>
                        <FolderOpenOutlined style={{ color: G.green }} />
                        <span>
                            <Trans t={t} i18nKey="legende.sinistres" count={groupes.length} components={[<strong style={{ color: G.neutral700 }} />]} />
                            {' '}<Trans t={t} i18nKey="legende.reglements" count={items.length} components={[<strong style={{ color: G.neutral700 }} />]} />
                            {' '}{t('legende.cliquez')}
                        </span>
                    </div>

                    {/* Tableau groupé */}
                    <div className="cb-card" style={{ padding: 0 }}>
                        <Table
                            className="cb-table"
                            dataSource={groupes}
                            columns={sinistresColumns}
                            rowKey="sinistreReference"
                            loading={loading}
                            rowClassName="pmt-sinistre-row"
                            expandable={{
                                expandedRowKeys: expandedRows,
                                onExpand: (expanded, record) => {
                                    setExpandedRows(expanded
                                        ? [...expandedRows, record.sinistreReference]
                                        : expandedRows.filter(k => k !== record.sinistreReference)
                                    );
                                },
                                expandedRowRender: record => (
                                    <PaiementsSousTableau paiements={record.paiements} />
                                ),
                                expandIcon: ({ expanded, onExpand, record }) => (
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={expanded
                                            ? <DownOutlined style={{ fontSize: 10, color: G.green }} />
                                            : <RightOutlined style={{ fontSize: 10, color: G.neutral400 }} />
                                        }
                                        onClick={e => onExpand(record, e)}
                                        style={{ marginRight: 4 }}
                                    />
                                ),
                            }}
                            onRow={(record) => ({
                                onClick: () => {
                                    const isOpen = expandedRows.includes(record.sinistreReference);
                                    setExpandedRows(isOpen
                                        ? expandedRows.filter(k => k !== record.sinistreReference)
                                        : [...expandedRows, record.sinistreReference]
                                    );
                                },
                            })}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50'],
                                showTotal: (total, range) =>
                                    t('tableau.pagination', { count: total, debut: range[0], fin: range[1] }),
                            }}
                            onChange={onTableChange}
                            locale={{
                                emptyText: (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <span style={{ color: G.neutral500 }}>{t('tableau.aucunReglement')}</span>
                                        }
                                    />
                                ),
                            }}
                        />
                    </div>

                </div>

                {/* Export flottant */}
                {/* Le positionnement flottant vient de `floating`, qui pose lui-meme
                    l'enveloppe et reserve la place en bas de page. L'enveloppe ecrite a la
                    main qui etait ici la doublait a 24 px au lieu de 32 : deux coins pour
                    un seul bouton, et le second gagnait. */}
                  <TableExportButton
                      dataLoader={fetchAll}
                      columns={[
                          { title: t('export.numeroSinistre'), dataIndex: 'sinistreReference', key: 'ref' },
                          { title: t('export.numeroPaiement'), dataIndex: 'numeroPaiement', key: 'num' },
                          { title: t('export.beneficiaire'), dataIndex: 'beneficiaire', key: 'ben' },
                          { title: t('export.montant'), dataIndex: 'montant', key: 'mt', format: 'currency', numeric: true },
                          { title: t('export.banque'), dataIndex: 'banqueCheque', key: 'bq' },
                          { title: t('export.dateEmission'), dataIndex: 'dateEmission', key: 'dem', format: 'date' },
                          { title: t('export.datePaiement'), dataIndex: 'datePaiement', key: 'dpay', format: 'date' },
                          { title: t('export.statut'), dataIndex: 'statut', key: 'st', export: v => STATUT_MAP[v]?.label || v || '' },
                      ]}
                      filename="paiements"
                      title={t('titre')}
                      subtitle={t(Object.values(filters).some(v => v !== undefined && v !== null && v !== '') ? 'export.sousTitreFiltre' : 'export.sousTitre', { count: pagination.total })}
                      backgroundColor="#1a6b3c"
                      background="#1a6b3c"
                      color="#ffffff"
                      floating="true"
                      logo={logoLocal}
                      count={pagination.total}
                      loadingLabel={t('export.preparation')}
                  />
            </Layout>
        </>
    );
}